import React, { memo, useCallback, useMemo } from "react";
import {
  TextFieldSts,
  Dropdown,
  DropdownItem,
} from "staysure-component-library";

import InputValidationWrapper from "@/components/atoms/input-validation-wrapper/input-validation-wrapper";
import Styles from "@/components/molecules/questions-container/organizer/organiser-answer/organiser-form.module.css";
import { smoothScrollToRef } from "@/utils/scrollUtils";

import {
  TravellersRequiredInformationContent,
  TravellerFormData,
  TravellerValidationState,
  ErrorDetails,
} from "../../types/travellers-required-information.type";

const DROPDOWN_LIST_BOX_STYLE = {
  outline: "none",
  margin: "0",
  minWidth: "unset !important",
  boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
};

interface TravellerSectionProps {
  traveller: TravellerFormData;
  validation: TravellerValidationState | undefined;
  errors:
    | { identificationType: string | null; identificationNumber: string | null }
    | undefined;
  isOptional: boolean;
  optionalText: string;
  content: TravellersRequiredInformationContent;
  identificationTypes: Array<{ id: string; label: string }>;
  updateTravellerField: (
    encryptedId: string,
    field: keyof TravellerFormData,
    value: string
  ) => void;
  setFieldTouched: (section: string, field: string) => void;
  errorDetails: ErrorDetails;
  firstErrorRef: React.RefObject<HTMLDivElement | null>;
  identificationTypeRef: React.RefObject<HTMLDivElement | null>;
  identificationNumberRef: React.RefObject<HTMLButtonElement | null>;
  prevFieldRef:
    | React.RefObject<HTMLDivElement | null>
    | React.RefObject<HTMLButtonElement | null>;
  hasMounted: React.MutableRefObject<boolean>;
  isLastTraveller: boolean;
}

const TravellerSection = memo(
  ({
    traveller,
    validation,
    errors,
    isOptional,
    optionalText,
    content,
    identificationTypes,
    updateTravellerField,
    setFieldTouched,
    errorDetails,
    firstErrorRef,
    identificationTypeRef,
    identificationNumberRef,
    prevFieldRef,
    hasMounted,
    isLastTraveller,
  }: TravellerSectionProps) => {
    const handleIdentificationTypeChange = useCallback(
      (value: string) => {
        updateTravellerField(
          traveller.encryptedId,
          "identificationType",
          value
        );
        setFieldTouched(traveller.encryptedId, "identificationType");

        // Scroll to identification number field after selection
        if (hasMounted.current) {
          smoothScrollToRef(identificationNumberRef, 100);
        }
      },
      [
        updateTravellerField,
        setFieldTouched,
        traveller.encryptedId,
        hasMounted,
        identificationNumberRef,
      ]
    );

    const handleIdentificationTypeBlur = useCallback(() => {
      setFieldTouched(traveller.encryptedId, "identificationType");
    }, [setFieldTouched, traveller.encryptedId]);

    const getIdentificationErrorMessage = useCallback(
      (
        errorKey: string | null,
        validationStatus: "success" | "error" | "warning" | null
      ): string => {
        if (!validationStatus || validationStatus === "success") return "";

        if (validationStatus === "warning") {
          const warnings =
            content?.organiserSection?.identificationSection?.warnings;
          return warnings?.numberWarning ?? "";
        }

        if (!errorKey) return "";
        const errors = content?.organiserSection?.identificationSection?.errors;
        return (
          errors?.[
            errorKey as
              | "identificationNumberInvalidFormat"
              | "typeRequiredForNumber"
          ] ?? ""
        );
      },
      [content]
    );

    const identificationNumberMessage = useMemo(
      () =>
        getIdentificationErrorMessage(
          errors?.identificationNumber ?? null,
          validation?.identificationNumber ?? null
        ),
      [
        errors?.identificationNumber,
        validation?.identificationNumber,
        getIdentificationErrorMessage,
      ]
    );

    const typeLabel = isOptional
      ? `${
          content?.organiserSection?.identificationSection?.typePlaceholder ??
          "Identification Type"
        } (${optionalText})`
      : content?.organiserSection?.identificationSection?.typePlaceholder ??
        "Identification Type";

    const numberLabel = isOptional
      ? `${
          content?.organiserSection?.identificationSection?.numberPlaceholder ??
          "Identification Number"
        } (${optionalText})`
      : content?.organiserSection?.identificationSection?.numberPlaceholder ??
        "Identification Number";

    return (
      <div className="mb-spacing-l">
        <p className="font-body-font text-xs text-text-body leading-27 font-semibold mb-spacing-s">
          {traveller.firstName} {traveller.lastName},{" "}
          <span className="font-semibold">{traveller.age}</span>
        </p>

        {/* Identification Type Dropdown */}
        <InputValidationWrapper
          className="flex flex-row mb-spacing-m"
          status={validation?.identificationType ?? null}
          childRef={
            errorDetails.firstError ===
            `traveller-${traveller.encryptedId}-identificationType`
              ? firstErrorRef
              : undefined
          }
        >
          <div
            ref={identificationTypeRef}
            className={`${Styles.country_wrapper} ${Styles.title_dropdown} flex flex-row items-start w-full h-[56px]`}
          >
            <Dropdown
              id={`traveller-identification-type-${traveller.encryptedId}`}
              defaultValue={traveller.identificationType}
              placeholderTextAndLabel={typeLabel}
              strokeColor="#3B3B3B"
              onSelectionChange={handleIdentificationTypeChange}
              onDropdownBlur={handleIdentificationTypeBlur}
              className={`${Styles.droplistBox} ${
                validation?.identificationType === "warning"
                  ? Styles.warningMsgClass
                  : ""
              } ${
                validation?.identificationType === "success"
                  ? Styles.successMsgClass
                  : ""
              } border-interactive-secondary-default rounded-radius-lg max-h-spacing-xxxl !py-spacing-m !px-spacing-l`}
              placeholderLeftBeforeSelect={0}
              placeholderLeftAfterSelect={isOptional ? -32 : -24}
              listBoxStyle={DROPDOWN_LIST_BOX_STYLE}
              listClassName={`${Styles.listStyle} mt-[10px] rounded-radius-s`}
              unorderedListClassName="w-full !mt-[3px] !md:ml-0 !ml-[-1px] !border-border-subdued !rounded-radius-lg !px-spacing-m !py-spacing-xs"
            >
              {identificationTypes.map((type) => (
                <DropdownItem key={type.id} value={type.id}>
                  <span className="w-full leading-[24px] font-proximanova text-2xs font-semibold p-spacing-xs">
                    {type.label}
                  </span>
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
        </InputValidationWrapper>

        {/* Identification Number */}
        <div className="mb-spacing-m">
          <button
            type="button"
            tabIndex={-1}
            ref={identificationNumberRef}
            onFocus={() => {
              if (hasMounted.current) {
                smoothScrollToRef(prevFieldRef, 100);
              }
            }}
            data-testid={`traveller-identification-number-scroll-id-${traveller.encryptedId}`}
            className="w-full focus-visible:outline-none"
          >
            <InputValidationWrapper
              className="flex items-start"
              status={validation?.identificationNumber ?? null}
              childRef={
                errorDetails.firstError ===
                `traveller-${traveller.encryptedId}-identificationNumber`
                  ? firstErrorRef
                  : undefined
              }
            >
              <TextFieldSts
                label={numberLabel}
                data-testid={`traveller-identification-number-${traveller.encryptedId}`}
                id={`traveller-identification-number-${traveller.encryptedId}`}
                width="100%"
                name="identificationNumber"
                maxLength={12}
                msgCustomStyles={{ width: "auto" }}
                state={validation?.identificationNumber ?? undefined}
                message={identificationNumberMessage}
                value={traveller.identificationNumber || ""}
                onChange={(value) =>
                  updateTravellerField(
                    traveller.encryptedId,
                    "identificationNumber",
                    value
                  )
                }
                onBlur={() => {
                  setFieldTouched(
                    traveller.encryptedId,
                    "identificationNumber"
                  );

                  // If this is the last traveller, scroll to Continue button
                  if (isLastTraveller && hasMounted.current) {
                    setTimeout(() => {
                      const element = document.getElementById(
                        "required-information-main-cta-button-id"
                      );
                      if (element) {
                        element.scrollIntoView({
                          behavior: "smooth",
                          block: "end",
                        });
                      }
                    }, 200);
                  }
                }}
                brand="staysure"
              />
            </InputValidationWrapper>
          </button>
        </div>
      </div>
    );
  }
);

TravellerSection.displayName = "TravellerSection";

export default TravellerSection;
